import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  useTheme,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const DateRelative = ({ onDateRangeSelect, navigateOnClick = false }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [selectedChip, setSelectedChip] = useState(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dateSelected, setDateSelected] = useState(false);

  // Helper function to format date as YYYY-MM-DD
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to format date for display
  const formatDisplayDate = (dateRange) => {
    if (dateRange.availableDate) {
      const date = new Date(dateRange.availableDate);
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } else if (dateRange.availableDateStart && dateRange.availableDateEnd) {
      const startDate = new Date(dateRange.availableDateStart);
      const endDate = new Date(dateRange.availableDateEnd);
      const startStr = startDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
      const endStr = endDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
      return `${startStr} - ${endStr}`;
    }
    return '';
  };

  // Calculate date ranges
  const getDateRanges = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    // This Week: Today to end of week (Sunday)
    const thisWeekStart = new Date(today);
    const thisWeekEnd = new Date(today);
    const dayOfWeek = today.getDay(); // 0 = Sunday, 6 = Saturday
    const daysUntilSunday = 7 - dayOfWeek;
    thisWeekEnd.setDate(today.getDate() + daysUntilSunday);

    // Next Week: Monday to Sunday of next week
    const nextWeekStart = new Date(today);
    const daysUntilNextMonday = (8 - dayOfWeek) % 7 || 7;
    nextWeekStart.setDate(today.getDate() + daysUntilNextMonday);
    
    const nextWeekEnd = new Date(nextWeekStart);
    nextWeekEnd.setDate(nextWeekStart.getDate() + 6);

    return {
      today: {
        availableDate: formatDate(today),
        availableDateStart: null,
        availableDateEnd: null,
      },
      tomorrow: {
        availableDate: formatDate(tomorrow),
        availableDateStart: null,
        availableDateEnd: null,
      },
      thisWeek: {
        availableDate: null,
        availableDateStart: formatDate(thisWeekStart),
        availableDateEnd: formatDate(thisWeekEnd),
      },
      nextWeek: {
        availableDate: null,
        availableDateStart: formatDate(nextWeekStart),
        availableDateEnd: formatDate(nextWeekEnd),
      },
    };
  };

  const handleChipClick = (chipType) => {
    // If "Select Date" chip is clicked, open the date picker dialog
    if (chipType === 'selectDate') {
      setDatePickerOpen(true);
      return;
    }

    const ranges = getDateRanges();
    const selectedRange = ranges[chipType];

    setSelectedChip(chipType);
    setDateSelected(true);

    // Call the parent callback with the date range
    if (onDateRangeSelect) {
      onDateRangeSelect({
        type: chipType,
        ...selectedRange,
      });
    }

    // Only navigate directly if navigateOnClick is true AND no parent callback is provided
    // Otherwise, let the parent handle navigation (which requires specialty selection)
    if (navigateOnClick && !onDateRangeSelect) {
      const params = new URLSearchParams();
      if (selectedRange.availableDate) {
        params.set('availableDate', selectedRange.availableDate);
      } else {
        if (selectedRange.availableDateStart) {
          params.set('availableDateStart', selectedRange.availableDateStart);
        }
        if (selectedRange.availableDateEnd) {
          params.set('availableDateEnd', selectedRange.availableDateEnd);
        }
      }
      const queryString = params.toString();
      navigate(`/find-doctors${queryString ? `?${queryString}` : ''}`);
    }
  };

  const handleDatePickerAccept = () => {
    if (selectedDate) {
      const formattedDate = formatDate(selectedDate);
      setSelectedChip('selectDate');
      setDateSelected(true);

      // Call the parent callback with the selected date
      if (onDateRangeSelect) {
        onDateRangeSelect({
          type: 'selectDate',
          availableDate: formattedDate,
          availableDateStart: null,
          availableDateEnd: null,
        });
      }

      // Navigate if needed
      if (navigateOnClick && !onDateRangeSelect) {
        const params = new URLSearchParams();
        params.set('availableDate', formattedDate);
        navigate(`/find-doctors?${params.toString()}`);
      }

      setDatePickerOpen(false);
    }
  };

  const handleDatePickerClose = () => {
    setDatePickerOpen(false);
    setSelectedDate(null);
  };

  const handleChangeDate = () => {
    setDatePickerOpen(true);
  };

  const dateChips = [
    { id: 'selectDate', label: 'Select Date' },
    // { id: 'today', label: 'Today' },
    // { id: 'tomorrow', label: 'Tomorrow' },
    // { id: 'thisWeek', label: 'This Week' },
    // { id: 'nextWeek', label: 'Next Week' },
    
  ];

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        bgcolor: theme.palette.background.default,
      }}
    >
      {!dateSelected ? (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 1,
              width: '100%',
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollBehavior: 'smooth',
              scrollbarWidth: 'thin',
              scrollbarColor: `${theme.palette.primary.main}40 transparent`,
              '&::-webkit-scrollbar': {
                height: 4,
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: `${theme.palette.primary.main}40`,
                borderRadius: 2,
                '&:hover': {
                  background: `${theme.palette.primary.main}60`,
                },
              },
              '@media (max-width: 600px)': {
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
                scrollbarWidth: 'none',
              },
            }}
          >
            {dateChips.map((chip) => (
              <Chip
                key={chip.id}
                label={chip.label}
                onClick={() => handleChipClick(chip.id)}
                sx={{
                  height: { xs: 32, md: 40 },
                  fontSize: { xs: '0.75rem', md: '0.8rem' },
                  fontWeight: 600,
                  borderRadius: '12px',
                  px: { xs: 1, md: 1.5 },
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  bgcolor:
                    selectedChip === chip.id
                      ? theme.palette.primary.main
                      : theme.palette.background.paper,
                  color:
                    selectedChip === chip.id
                      ? 'white'
                      : theme.palette.text.primary,
                  border: `1.5px solid ${
                    selectedChip === chip.id
                      ? theme.palette.primary.main
                      : theme.palette.divider
                  }`,
                  boxShadow:
                    selectedChip === chip.id
                      ? `0 2px 8px ${theme.palette.primary.main}30`
                      : 'none',
                  '&:hover': {
                    bgcolor:
                      selectedChip === chip.id
                        ? theme.palette.primary.dark
                        : theme.palette.action.hover,
                    borderColor: theme.palette.primary.main,
                    boxShadow: `0 2px 8px ${
                      selectedChip === chip.id
                        ? theme.palette.primary.main
                        : 'rgba(0,0,0,0.1)'
                    }`,
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ width: '100%' }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {formatDisplayDate(getDateRanges()[selectedChip] || {
                availableDate: selectedDate ? formatDate(selectedDate) : null,
                availableDateStart: null,
                availableDateEnd: null,
              })}
            </Typography>
            <Button
              onClick={handleChangeDate}
              variant="outlined"
              size="small"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '12px',
                fontSize: { xs: '0.7rem', md: '0.75rem' },
                px: { xs: 1, md: 1.5 },
                py: { xs: 0.5, md: 0.75 },
                minWidth: 'auto',
                height: { xs: 28, md: 32 },
              }}
            >
              Change
            </Button>
          </Stack>
        </Box>
      )}

      {/* Date Picker Dialog */}
      <Dialog
        open={datePickerOpen}
        onClose={handleDatePickerClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: { xs: '1.1rem', md: '1.25rem' },
            pb: 1,
          }}
        >
          Select Date
        </DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Choose a date"
              value={selectedDate}
              onChange={(newValue) => setSelectedDate(newValue)}
              minDate={new Date()}
              slotProps={{
                textField: {
                  fullWidth: true,
                  sx: {
                    mt: 1,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleDatePickerClose}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDatePickerAccept}
            variant="contained"
            disabled={!selectedDate}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
            }}
          >
            Select
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DateRelative;

